import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db, auth } from '@municipality/firebase';
import { User } from '@municipality/types';
import { SuperAdminRole } from '@municipality/types';
import { useAuth } from './useAuth';

export interface AllUser extends User {
  municipalityName?: string;
  lastLogin?: Date;
  status: 'active' | 'inactive' | 'pending';
  municipalityId?: string;
  name?: string;
  surname?: string;
  superAdminRole?: string;
  invitedBy?: string;
  type: 'user';
}

export interface PendingInvitation {
  id: string;
  email: string;
  name?: string;
  surname?: string;
  superAdminRole?: string;
  municipalityName?: string;
  municipalityId?: string;
  role: 'municipality_admin' | 'municipality_user' | 'super_admin';
  status: 'pending' | 'accepted' | 'expired';
  createdAt: Date;
  expiresAt: Date;
  adminName?: string;
  municipalityRole?: string;
  invitedBy?: string;
  type: 'invitation';
}

export type UserOrInvitation = AllUser | PendingInvitation;

// Helper function to convert createdAt to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  // If it has a toDate method (Firestore Timestamp)
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

export const useAllUsersAndInvitations = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AllUser[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [allItems, setAllItems] = useState<UserOrInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isRegionManager =
    (currentUser as { superAdminRole?: string } | null)?.superAdminRole ===
    SuperAdminRole.REGION_MANAGER;

  useEffect(() => {
    console.log('🔍 DEBUG: useAllUsersAndInvitations hook initialized');
    console.log('🔍 DEBUG: Current auth user:', auth.currentUser?.uid);
    console.log('🔍 DEBUG: Current auth email:', auth.currentUser?.email);
    
    // Fetch ONLY super admin users (not municipality users or citizens)
    const usersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'super_admin'),
      orderBy('createdAt', 'desc')
    );

    const usersUnsubscribe = onSnapshot(
      usersQuery,
      (snapshot) => {
        const usersData: AllUser[] = [];
        
        snapshot.forEach((doc) => {
          const userData = doc.data() as AllUser;
          usersData.push({
            ...userData,
            id: doc.id,
            status: userData.status || 'active',
            type: 'user' as const,
            createdAt: toDate(userData.createdAt),
            updatedAt: toDate(userData.updatedAt),
            lastLogin: userData.lastLogin ? toDate(userData.lastLogin) : undefined
          });
        });

        console.log('🔍 DEBUG: Super admin users fetched:', usersData.length);
        setUsers(usersData);
      },
      (err) => {
        console.error('Error fetching super admin users:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Fetch invitations - ONLY super admin invitations
    console.log('🔍 DEBUG: Setting up invitations listener');
    const invitationsQuery = query(
      collection(db, 'invitations'),
      where('role', '==', 'super_admin')
    );

    const invitationsUnsubscribe = onSnapshot(
      invitationsQuery,
      (snapshot) => {
        console.log('🔍 DEBUG: Super admin invitations snapshot received:', snapshot.size, 'documents');
        const invitationsData: PendingInvitation[] = [];
        
        snapshot.forEach((doc) => {
          const invitationData = doc.data();
          console.log('🔍 DEBUG: Processing invitation:', doc.id, invitationData.status);
          invitationsData.push({
            ...invitationData,
            id: doc.id,
            createdAt: toDate(invitationData.createdAt),
            expiresAt: toDate(invitationData.expiresAt),
            type: 'invitation'
          } as PendingInvitation);
        });

        // Sort invitations by createdAt descending
        invitationsData.sort((a, b) => {
          const aDate = toDate(a.createdAt);
          const bDate = toDate(b.createdAt);
          return bDate.getTime() - aDate.getTime();
        });

        console.log('🔍 DEBUG: Total super admin invitations processed:', invitationsData.length);
        console.log('🔍 DEBUG: Pending super admin invitations:', invitationsData.filter(inv => inv.status === 'pending').length);
        setInvitations(invitationsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('❌ DEBUG: Error fetching invitations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      usersUnsubscribe();
      invitationsUnsubscribe();
    };
  }, []);

  // Filter users and invitations for Region Managers (only their own invitees)
  const filteredUsers = useMemo(() => {
    if (!isRegionManager || !currentUser?.uid) return users;
    return users.filter(
      (u) => u.id === currentUser.uid || u.invitedBy === currentUser.uid
    );
  }, [users, isRegionManager, currentUser?.uid]);

  const filteredInvitations = useMemo(() => {
    const pending = invitations.filter((inv) => inv.status === 'pending');
    if (!isRegionManager || !currentUser?.uid) return pending;
    return pending.filter((inv) => inv.invitedBy === currentUser.uid);
  }, [invitations, isRegionManager, currentUser?.uid]);

  // Combine users and invitations
  useEffect(() => {
    const combined = [...filteredUsers, ...filteredInvitations].sort(
      (a, b) => {
        const aDate = toDate(a.createdAt);
        const bDate = toDate(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      }
    );
    setAllItems(combined);
  }, [filteredUsers, filteredInvitations]);

  return {
    users: filteredUsers,
    invitations: filteredInvitations,
    allItems,
    loading,
    error,
  };
};

export const useUsersByRole = (role: string) => {
  const { users, loading, error } = useAllUsersAndInvitations();
  
  const filteredUsers = users.filter(user => user.role === role);
  
  return { users: filteredUsers, loading, error };
};

export const useUsersByMunicipality = (municipalityId: string) => {
  const { users, loading, error } = useAllUsersAndInvitations();
  
  const filteredUsers = users.filter(user => user.municipalityId === municipalityId);
  
  return { users: filteredUsers, loading, error };
};
